import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import {
  DateRange,
  PickersShortcutsProps,
  PickersShortcutsItem,
} from '@mui/x-date-pickers-pro';
import {
  subDays,
  subWeeks,
  subMonths,
  startOfMonth,
  startOfDay,
  addDays,
  startOfWeek,
  endOfDay,
  format as formatDate,
} from 'date-fns';
import {
  Box,
  Button,
  FormControl,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';
import { toast } from 'react-toastify';

import './filter.css';
import useDraftDateRange from '../../hooks/global/useDraftDateRange';

const INVALID_DATE_TOAST_ID = 'INVALID_DATE_TOAST_ID';

const SHORTCUTS: PickersShortcutsItem<DateRange<Date>>[] = [
  {
    label: 'Last 7 Days',
    getValue: () => {
      const today = new Date();
      return [startOfDay(subDays(today, 7)), endOfDay(subDays(today, 1))];
    },
  },
  {
    label: 'This Month',
    getValue: () => {
      const today = new Date();
      return [startOfDay(startOfMonth(today)), today];
    },
  },
  {
    label: 'Last Week',
    getValue: () => {
      const lastSunday = subWeeks(startOfWeek(new Date()), 1);
      return [lastSunday, endOfDay(addDays(lastSunday, 6))];
    },
  },
  {
    label: 'Last Month',
    getValue: () => {
      const startOfThisMonth = startOfMonth(new Date());
      return [
        startOfDay(subMonths(startOfThisMonth, 1)),
        endOfDay(subDays(startOfThisMonth, 1)),
      ];
    },
  },
];

const popperPaperSx: SxProps<Theme> = theme => ({
  borderRadius: '14px',
  border: `1px solid ${theme.palette.lightGrey.main}`,
  boxShadow:
    '0 24px 48px -16px rgba(17, 17, 17, 0.18), 0 2px 6px rgba(17, 17, 17, 0.04)',
  overflow: 'hidden',
  fontFamily: 'Montserrat, sans-serif',
  maxWidth: 'calc(100vw - 16px)',
  '& .MuiPickersDay-root': {
    color: 'text.primary',
    typography: 'subtitle2',
  },
  '& .MuiPickersDay-root.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: 'common.white',
    fontWeight: 600,
  },
  '& .MuiPickersDay-root.Mui-selected:hover': { backgroundColor: '#D62700' },
  '& .MuiDateRangePickerDay-day.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: 'common.white',
  },
  '& .MuiPickersDay-today:not(.Mui-selected)': {
    border: `1px solid ${theme.palette.outlineGrey.main}`,
  },
  '& .MuiPickersCalendarHeader-label': {
    fontWeight: 600,
    color: 'text.primary',
  },
  '& .MuiDayCalendar-weekDayLabel': {
    color: 'text.secondary',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    typography: 'overline',
  },
});

const presetMenuPaperSx: SxProps<Theme> = theme => ({
  mt: -1,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.lightGrey.main}`,
  boxShadow:
    '0 16px 32px -12px rgba(17, 17, 17, 0.18), 0 2px 6px rgba(17, 17, 17, 0.04)',
  minWidth: 180,
});

function CalendarIcon() {
  return (
    <img
      src="/calendar.svg"
      alt="calendar"
      style={{ width: '14px', marginRight: '10px' }}
    />
  );
}

// Displays the committed range only; the picker's draft never leaks here.
type CommittedFieldExtras = {
  committedLabel: string;
  onOpenPicker: () => void;
};

type CommittedFieldProps = CommittedFieldExtras & {
  inputRef?: React.Ref<HTMLInputElement>;
  InputProps?: { ref?: React.Ref<HTMLDivElement> };
};

const CommittedField = React.forwardRef<HTMLDivElement, CommittedFieldProps>(
  (
    { committedLabel, onOpenPicker, inputRef, InputProps: pickerInputProps },
    ref,
  ) => (
    <TextField
      ref={ref}
      className="date-range-picker"
      inputRef={inputRef}
      onClick={onOpenPicker}
      placeholder="Select Dates"
      value={committedLabel}
      InputProps={{
        ...pickerInputProps,
        startAdornment: <CalendarIcon />,
        readOnly: true,
      }}
      // eslint-disable-next-line react/jsx-no-duplicate-props
      inputProps={{
        className: 'date-range-picker-input',
        'aria-label': 'Date range',
      }}
      InputLabelProps={{ shrink: true }}
    />
  ),
);
// single-input so MUI X attaches the popper anchor via InputProps.ref.
(CommittedField as unknown as { fieldType: string }).fieldType = 'single-input';

function SidebarShortcuts(
  props: PickersShortcutsProps<DateRange<Date>> & { className?: string },
) {
  const { items, onChange, isValid, className } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Returning null collapses the sidebar's grid column on mobile.
  if (!items?.length || isMobile) return null;

  return (
    <Stack
      className={className}
      sx={t => ({
        gridRow: '1 / 4',
        gridColumn: 1,
        borderRight: `1px solid ${t.palette.lightGrey.main}`,
        backgroundColor: '#FAFAFA',
        px: 1,
        py: 2,
        gap: 0.25,
        minWidth: 156,
      })}>
      <Typography
        variant="caption"
        component="p"
        fontWeight={600}
        sx={{
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'text.secondary',
          px: 1.5,
          pb: 1,
        }}>
        Quick Ranges
      </Typography>
      {items.map(item => {
        const newValue = item.getValue({ isValid });
        return (
          <Button
            key={item.label}
            variant="text"
            disableRipple
            disabled={!isValid(newValue)}
            // 'set' (vs 'accept') keeps the picker open after click.
            onClick={() => onChange(newValue, 'set', item)}
            sx={t => ({
              justifyContent: 'flex-start',
              textTransform: 'none',
              borderRadius: '8px',
              px: 1.5,
              py: 0.75,
              color: 'text.primary',
              transition: 'background-color 120ms ease, color 120ms ease',
              '&:hover': {
                backgroundColor: '#FFFFFF',
                color: t.palette.primary.main,
              },
              typography: 'subtitle2',
            })}>
            {item.label}
          </Button>
        );
      })}
    </Stack>
  );
}

type ActionBarExtras = {
  hasCapError: boolean;
  maxDateRangeMonths?: number | null;
  isMobile: boolean;
  applyDisabled: boolean;
  onPresetSelect: (item: PickersShortcutsItem<DateRange<Date>>) => void;
  onClear: () => void;
  onApply: () => void;
};

type PickerActionBarProps = ActionBarExtras & { className?: string };

function PickerActionBar({
  className,
  hasCapError,
  maxDateRangeMonths,
  isMobile,
  applyDisabled,
  onPresetSelect,
  onClear,
  onApply,
}: PickerActionBarProps) {
  const [presetAnchor, setPresetAnchor] = React.useState<HTMLElement | null>(
    null,
  );
  return (
    <Stack
      className={className}
      sx={t => ({
        px: 2,
        py: 1.25,
        borderTop: `1px solid ${t.palette.lightGrey.main}`,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1.5,
        rowGap: 1,
        flexWrap: 'wrap',
      })}>
      {hasCapError ? (
        <Box
          sx={{
            flexBasis: '100%',
            textAlign: 'right',
            color: 'error.dark',
            letterSpacing: '0.01em',
            typography: 'caption',
            fontWeight: 600,
          }}>
          Max {maxDateRangeMonths} month
          {maxDateRangeMonths === 1 ? '' : 's'}. Shorten the range to apply
        </Box>
      ) : null}
      {isMobile ? (
        <Button
          variant="light"
          color="info"
          onClick={e => setPresetAnchor(e.currentTarget)}
          sx={{
            textTransform: 'none',
            typography: 'subtitle2',
          }}>
          Presets
        </Button>
      ) : null}
      <Box sx={{ flex: 1 }} />
      <Button variant="link" onClick={onClear}>
        Clear
      </Button>
      <Button variant="base" disabled={applyDisabled} onClick={onApply}>
        Apply
      </Button>
      <Menu
        anchorEl={presetAnchor}
        open={Boolean(presetAnchor)}
        onClose={() => setPresetAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            // Stop mousedown bubbling to document — the picker's
            // ClickAwayListener would otherwise see the click as outside and close.
            onMouseDown: e => e.stopPropagation(),
            sx: presetMenuPaperSx,
          },
        }}>
        {SHORTCUTS.map(item => (
          <MenuItem
            key={item.label}
            onClick={() => {
              onPresetSelect(item);
              setPresetAnchor(null);
            }}
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              typography: 'subtitle2',
            }}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
}

// MUI X's slot types don't model our custom extras — cast at the boundary.
type DateRangePickerSlots = React.ComponentProps<
  typeof DateRangePicker
>['slots'];
type DateRangePickerSlotProps = React.ComponentProps<
  typeof DateRangePicker
>['slotProps'];

const pickerSlots = {
  shortcuts: SidebarShortcuts,
  field: CommittedField,
  actionBar: PickerActionBar,
} as unknown as DateRangePickerSlots;

type Props = {
  storageKey: string;
  value: DateRange<Date>;
  setValue: (value: DateRange<Date>) => void;
  maxDateRangeMonths?: number | null;
};

export default function MyDateRangePicker({
  storageKey,
  value,
  setValue,
  maxDateRangeMonths,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState(false);

  const { draft, setDraft, hasCap, isWithinCap, reset, clear, apply } =
    useDraftDateRange({ value, setValue, storageKey, maxDateRangeMonths });

  const openPicker = React.useCallback(() => {
    reset();
    setOpen(true);
  }, [reset]);

  const closePicker = React.useCallback(() => {
    reset();
    setOpen(false);
  }, [reset]);

  const handleApply = React.useCallback(() => {
    apply();
    setError(false);
    setOpen(false);
  }, [apply]);

  const handlePresetSelect = React.useCallback(
    (item: PickersShortcutsItem<DateRange<Date>>) => {
      setDraft(item.getValue({ isValid: () => true }));
    },
    [setDraft],
  );

  const handleRangeChange = React.useCallback(
    (
      newValue: DateRange<Date>,
      context: { validationError?: (string | null)[] },
    ) => {
      const isInvalid = !!context.validationError?.filter(Boolean).length;
      if (isInvalid) {
        setError(true);
        if (!toast.isActive(INVALID_DATE_TOAST_ID)) {
          toast.error('Invalid date range', {
            toastId: INVALID_DATE_TOAST_ID,
          });
        }
        return;
      }
      setError(false);
      setDraft(newValue);
    },
    [setDraft],
  );

  const committedLabel = React.useMemo(() => {
    if (!value[0] || !value[1]) return '';
    return `${formatDate(value[0], 'MMM d')} – ${formatDate(
      value[1],
      'MMM d',
    )}`;
  }, [value]);

  return (
    <FormControl
      error={error}
      sx={{ m: 1, width: 164 }}
      style={{ marginLeft: 0 }}>
      <DateRangePicker
        open={open}
        onOpen={openPicker}
        onClose={closePicker}
        closeOnSelect={false}
        // Force popper variant — devtools touch emulation would swap in the mobile dialog.
        desktopModeMediaQuery="@media (min-width: 0px)"
        value={draft}
        onChange={handleRangeChange}
        calendars={1}
        className="date-range-picker"
        format="MMM d"
        slots={pickerSlots}
        slotProps={
          {
            shortcuts: { items: SHORTCUTS },
            actionBar: {
              hasCapError: hasCap && !isWithinCap,
              maxDateRangeMonths,
              isMobile,
              applyDisabled: !isWithinCap,
              onPresetSelect: handlePresetSelect,
              onClear: clear,
              onApply: handleApply,
            },
            field: {
              committedLabel,
              onOpenPicker: openPicker,
            },
            desktopPaper: { sx: popperPaperSx },
          } as DateRangePickerSlotProps
        }
      />
    </FormControl>
  );
}
